import React, { useState, useRef } from 'react';
import { Plus, Trash2, ChevronRight, ChevronDown, Users, DollarSign, Download, FileImage } from 'lucide-react';

export default function SimuladorPlantaMunicipal() {
  const [niveles, setNiveles] = useState([
    { id: 1, nombre: 'Alcalde', orden: 1, salario: 0 },
    { id: 2, nombre: 'Secretarios', orden: 2, salario: 0 },
    { id: 3, nombre: 'Profesionales', orden: 3, salario: 0 }
  ]);
  
  const [dependencias, setDependencias] = useState([
    { id: 1, nombre: 'Despacho del Alcalde', parentId: null, nivel: 1, personal: {} }
  ]);
  
  const [vistaActual, setVistaActual] = useState('niveles');
  const [nuevoNivel, setNuevoNivel] = useState('');
  const [nuevaDependencia, setNuevaDependencia] = useState({ nombre: '', parentId: null });
  const [dependenciaExpandida, setDependenciaExpandida] = useState({});
  const organigramaRef = useRef(null);

  const agregarNivel = () => {
    if (nuevoNivel.trim()) {
      const nuevoOrden = Math.max(...niveles.map(n => n.orden), 0) + 1;
      setNiveles([...niveles, { 
        id: Date.now(), 
        nombre: nuevoNivel, 
        orden: nuevoOrden,
        salario: 0 
      }]);
      setNuevoNivel('');
    }
  };

  const eliminarNivel = (id) => {
    if (niveles.length > 3) {
      setNiveles(niveles.filter(n => n.id !== id));
    } else {
      alert('Debe mantener al menos 3 niveles');
    }
  };

  const moverNivel = (id, direccion) => {
    const index = niveles.findIndex(n => n.id === id);
    if (direccion === 'arriba' && index > 0) {
      const nuevosNiveles = [...niveles];
      [nuevosNiveles[index], nuevosNiveles[index - 1]] = [nuevosNiveles[index - 1], nuevosNiveles[index]];
      nuevosNiveles.forEach((n, i) => n.orden = i + 1);
      setNiveles(nuevosNiveles);
    } else if (direccion === 'abajo' && index < niveles.length - 1) {
      const nuevosNiveles = [...niveles];
      [nuevosNiveles[index], nuevosNiveles[index + 1]] = [nuevosNiveles[index + 1], nuevosNiveles[index]];
      nuevosNiveles.forEach((n, i) => n.orden = i + 1);
      setNiveles(nuevosNiveles);
    }
  };

  const actualizarSalario = (id, salario) => {
    setNiveles(niveles.map(n => n.id === id ? { ...n, salario: parseFloat(salario) || 0 } : n));
  };

  const agregarDependencia = () => {
    if (nuevaDependencia.nombre.trim()) {
      const parentNivel = nuevaDependencia.parentId ? 
        obtenerNivelDependencia(nuevaDependencia.parentId) : 0;
      
      if (parentNivel < 3) {
        setDependencias([...dependencias, {
          id: Date.now(),
          nombre: nuevaDependencia.nombre,
          parentId: nuevaDependencia.parentId,
          nivel: parentNivel + 1,
          personal: {}
        }]);
        setNuevaDependencia({ nombre: '', parentId: null });
      } else {
        alert('Solo se permiten hasta 3 niveles de dependencias');
      }
    }
  };

  const obtenerNivelDependencia = (id) => {
    const dep = dependencias.find(d => d.id === id);
    if (!dep) return 0;
    return dep.nivel;
  };

  const eliminarDependencia = (id) => {
    const tieneHijos = dependencias.some(d => d.parentId === id);
    if (tieneHijos) {
      alert('No puede eliminar una dependencia que tiene subdependencias');
      return;
    }
    if (dependencias.length > 1) {
      setDependencias(dependencias.filter(d => d.id !== id));
    } else {
      alert('Debe mantener al menos una dependencia');
    }
  };

  const actualizarPersonal = (depId, nivelId, cantidad) => {
    setDependencias(dependencias.map(d => {
      if (d.id === depId) {
        return {
          ...d,
          personal: { ...d.personal, [nivelId]: parseInt(cantidad) || 0 }
        };
      }
      return d;
    }));
  };

  const calcularCostoTotal = () => {
    let total = 0;
    dependencias.forEach(dep => {
      Object.keys(dep.personal).forEach(nivelId => {
        const nivel = niveles.find(n => n.id === parseInt(nivelId));
        if (nivel) {
          total += (dep.personal[nivelId] || 0) * nivel.salario * 12;
        }
      });
    });
    return total;
  };

  const calcularCostoDependencia = (depId) => {
    const dep = dependencias.find(d => d.id === depId);
    if (!dep) return 0;
    let total = 0;
    Object.keys(dep.personal).forEach(nivelId => {
      const nivel = niveles.find(n => n.id === parseInt(nivelId));
      if (nivel) {
        total += (dep.personal[nivelId] || 0) * nivel.salario * 12;
      }
    });
    return total;
  };

  // Organigrama solo visual (sin inputs)
  const renderizarOrganigramaVisual = (dep, profundidad = 0) => {
    const hijos = dependencias.filter(d => d.parentId === dep.id);
    const expandida = dependenciaExpandida[dep.id] !== false;
    const totalPersonal = Object.values(dep.personal).reduce((a, b) => a + b, 0);

    return (
      <div key={dep.id} className="mb-4">
        <div className={`${profundidad > 0 ? 'ml-12 relative' : ''}`}>
          {profundidad > 0 && (
            <div className="absolute left-0 top-1/2 w-8 h-0.5 bg-green-400"></div>
          )}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg p-4 shadow-lg border-2 border-green-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {hijos.length > 0 && (
                  <button
                    onClick={() => setDependenciaExpandida({ ...dependenciaExpandida, [dep.id]: !expandida })}
                    className="text-white hover:text-green-200 bg-green-800 rounded p-1"
                  >
                    {expandida ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </button>
                )}
                <div>
                  <span className="font-bold text-lg">{dep.nombre}</span>
                  {totalPersonal > 0 && (
                    <span className="ml-2 text-sm bg-white text-green-800 px-3 py-1 rounded-full">
                      {totalPersonal} {totalPersonal === 1 ? 'persona' : 'personas'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {expandida && hijos.length > 0 && (
          <div className="ml-4 mt-2 border-l-2 border-green-300 pl-4">
            {hijos.map(hijo => renderizarOrganigramaVisual(hijo, profundidad + 1))}
          </div>
        )}
      </div>
    );
  };

  // Organigrama con inputs para edición
  const renderizarDependenciaEditable = (dep, profundidad = 0) => {
    const hijos = dependencias.filter(d => d.parentId === dep.id);
    const expandida = dependenciaExpandida[dep.id];
    const totalPersonal = Object.values(dep.personal).reduce((a, b) => a + b, 0);

    return (
      <div key={dep.id} className="mb-4">
        <div className={`${profundidad > 0 ? 'ml-12 relative' : ''}`}>
          {profundidad > 0 && (
            <div className="absolute left-0 top-1/2 w-8 h-0.5 bg-green-300"></div>
          )}
          <div className="bg-gradient-to-r from-green-50 to-white border-2 border-green-300 rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {hijos.length > 0 && (
                  <button
                    onClick={() => setDependenciaExpandida({ ...dependenciaExpandida, [dep.id]: !expandida })}
                    className="text-green-700 hover:text-green-900 bg-green-100 rounded p-1"
                  >
                    {expandida ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </button>
                )}
                <div>
                  <span className="font-bold text-green-900 text-lg">{dep.nombre}</span>
                  <span className="ml-2 text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full">
                    {totalPersonal} {totalPersonal === 1 ? 'persona' : 'personas'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => eliminarDependencia(dep.id)}
                className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-2"
              >
                <Trash2 size={18} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mt-3 bg-white p-3 rounded border border-green-200">
              {niveles.map(nivel => (
                <div key={nivel.id} className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 font-medium flex-1">{nivel.nombre}:</label>
                  <input
                    type="number"
                    min="0"
                    value={dep.personal[nivel.id] || 0}
                    onChange={(e) => actualizarPersonal(dep.id, nivel.id, e.target.value)}
                    className="w-20 px-2 py-1 border border-green-300 rounded text-sm focus:border-green-500 focus:outline-none"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {expandida && hijos.length > 0 && (
          <div className="ml-4 mt-2 border-l-2 border-green-200 pl-4">
            {hijos.map(hijo => renderizarDependenciaEditable(hijo, profundidad + 1))}
          </div>
        )}
      </div>
    );
  };

  const exportarOrganigramaPNG = async () => {
    const element = organigramaRef.current;
    if (!element) return;

    try {
      const html2canvas = window.html2canvas;
      if (!html2canvas) {
        alert('Cargando librería de exportación...');
        return;
      }

      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2
      });
      
      const link = document.createElement('a');
      link.download = 'organigrama-municipal.png';
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      alert('Error al exportar. Por favor intente nuevamente.');
    }
  };

  const exportarTablaExcel = (datos, nombreArchivo) => {
    let csv = '';
    
    // Agregar encabezados
    const headers = Object.keys(datos[0]);
    csv += headers.join(',') + '\n';
    
    // Agregar filas
    datos.forEach(fila => {
      const valores = headers.map(header => {
        const valor = fila[header];
        // Escapar comas y comillas
        if (typeof valor === 'string' && (valor.includes(',') || valor.includes('"'))) {
          return `"${valor.replace(/"/g, '""')}"`;
        }
        return valor;
      });
      csv += valores.join(',') + '\n';
    });

    // Crear blob y descargar
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = nombreArchivo + '.csv';
    link.click();
  };

  const exportarPresupuestoNivel = () => {
    const datos = niveles.map(nivel => {
      const totalPersonas = dependencias.reduce((sum, dep) => sum + (dep.personal[nivel.id] || 0), 0);
      const costoMensual = totalPersonas * nivel.salario;
      const costoAnual = costoMensual * 12;
      
      return {
        'Nivel': nivel.nombre,
        'Numero de Personas': totalPersonas,
        'Salario Mensual': nivel.salario,
        'Costo Mensual Total': costoMensual,
        'Costo Anual Total': costoAnual
      };
    });

    // Agregar total
    datos.push({
      'Nivel': 'TOTAL GENERAL',
      'Numero de Personas': '',
      'Salario Mensual': '',
      'Costo Mensual Total': calcularCostoTotal() / 12,
      'Costo Anual Total': calcularCostoTotal()
    });

    exportarTablaExcel(datos, 'presupuesto-por-nivel');
  };

  const exportarPresupuestoDependencia = () => {
    const datos = dependencias.map(dep => {
      const totalPersonal = Object.values(dep.personal).reduce((a, b) => a + b, 0);
      const costoAnual = calcularCostoDependencia(dep.id);
      const costoMensual = costoAnual / 12;

      return {
        'Dependencia': dep.nombre,
        'Nivel Jerarquico': dep.nivel === 1 ? 'Principal' : `Nivel ${dep.nivel}`,
        'Total Personal': totalPersonal,
        'Costo Mensual': costoMensual,
        'Costo Anual': costoAnual
      };
    });

    // Agregar total
    datos.push({
      'Dependencia': 'TOTAL GENERAL',
      'Nivel Jerarquico': '',
      'Total Personal': dependencias.reduce((sum, dep) => sum + Object.values(dep.personal).reduce((a, b) => a + b, 0), 0),
      'Costo Mensual': calcularCostoTotal() / 12,
      'Costo Anual': calcularCostoTotal()
    });

    exportarTablaExcel(datos, 'presupuesto-por-dependencia');
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-CO', { 
      style: 'currency', 
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor);
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-grow p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-8 rounded-lg shadow-lg mb-6">
            <h1 className="text-4xl font-bold">
              Simulador de Planta Municipal
            </h1>
            <p className="mt-2 text-green-100">Sistema de cálculo y visualización de estructura organizacional</p>
          </div>

          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setVistaActual('niveles')}
              className={`px-6 py-2 rounded-t font-medium ${
                vistaActual === 'niveles' 
                  ? 'bg-white text-green-700 border-t-4 border-green-600' 
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              1. Niveles y Salarios
            </button>
            <button
              onClick={() => setVistaActual('dependencias')}
              className={`px-6 py-2 rounded-t font-medium ${
                vistaActual === 'dependencias' 
                  ? 'bg-white text-green-700 border-t-4 border-green-600' 
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              2. Dependencias
            </button>
            <button
              onClick={() => setVistaActual('organigrama')}
              className={`px-6 py-2 rounded-t font-medium ${
                vistaActual === 'organigrama' 
                  ? 'bg-white text-green-700 border-t-4 border-green-600' 
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              3. Organigrama
            </button>
            <button
              onClick={() => setVistaActual('presupuestoNivel')}
              className={`px-6 py-2 rounded-t font-medium ${
                vistaActual === 'presupuestoNivel' 
                  ? 'bg-white text-green-700 border-t-4 border-green-600' 
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              4. Presupuesto por Nivel
            </button>
            <button
              onClick={() => setVistaActual('presupuestoDep')}
              className={`px-6 py-2 rounded-t font-medium ${
                vistaActual === 'presupuestoDep' 
                  ? 'bg-white text-green-700 border-t-4 border-green-600' 
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              5. Presupuesto por Dependencia
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            {vistaActual === 'niveles' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Configurar Niveles Jerárquicos</h2>
                
                <div className="mb-6">
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={nuevoNivel}
                      onChange={(e) => setNuevoNivel(e.target.value)}
                      placeholder="Nombre del nuevo nivel"
                      className="flex-1 px-4 py-2 border rounded"
                      onKeyPress={(e) => e.key === 'Enter' && agregarNivel()}
                    />
                    <button
                      onClick={agregarNivel}
                      className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 flex items-center gap-2"
                    >
                      <Plus size={20} /> Agregar Nivel
                    </button>
                  </div>

                  <div className="space-y-2">
                    {niveles.map((nivel, index) => (
                      <div key={nivel.id} className="flex items-center gap-3 p-3 border rounded bg-gray-50">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => moverNivel(nivel.id, 'arriba')}
                            disabled={index === 0}
                            className="text-gray-600 disabled:text-gray-300 hover:text-green-600"
                          >
                            ▲
                          </button>
                          <button
                            onClick={() => moverNivel(nivel.id, 'abajo')}
                            disabled={index === niveles.length - 1}
                            className="text-gray-600 disabled:text-gray-300 hover:text-green-600"
                          >
                            ▼
                          </button>
                        </div>
                        
                        <div className="flex-1">
                          <span className="font-semibold text-gray-700">{nivel.orden}. {nivel.nombre}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <DollarSign size={20} className="text-green-600" />
                          <input
                            type="number"
                            value={nivel.salario || ''}
                            onChange={(e) => actualizarSalario(nivel.id, e.target.value)}
                            placeholder="Salario mensual"
                            className="w-40 px-3 py-2 border rounded"
                          />
                        </div>
                        
                        <button
                          onClick={() => eliminarNivel(nivel.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {vistaActual === 'dependencias' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Crear Estructura de Dependencias</h2>
                
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded">
                  <h3 className="font-semibold mb-3">Agregar Nueva Dependencia</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={nuevaDependencia.nombre}
                      onChange={(e) => setNuevaDependencia({ ...nuevaDependencia, nombre: e.target.value })}
                      placeholder="Nombre de la dependencia"
                      className="flex-1 px-4 py-2 border rounded"
                    />
                    <select
                      value={nuevaDependencia.parentId || ''}
                      onChange={(e) => setNuevaDependencia({ ...nuevaDependencia, parentId: e.target.value ? parseInt(e.target.value) : null })}
                      className="px-4 py-2 border rounded"
                    >
                      <option value="">Dependencia principal</option>
                      {dependencias.filter(d => d.nivel < 3).map(d => (
                        <option key={d.id} value={d.id}>
                          Bajo: {d.nombre}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={agregarDependencia}
                      className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 flex items-center gap-2"
                    >
                      <Plus size={20} /> Agregar
                    </button>
                  </div>
                </div>

                <div>
                  {dependencias.filter(d => d.parentId === null).map(dep => renderizarDependenciaEditable(dep))}
                </div>
              </div>
            )}

            {vistaActual === 'organigrama' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-green-800">Organigrama Institucional</h2>
                  <button
                    onClick={exportarOrganigramaPNG}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
                  >
                    <FileImage size={20} /> Exportar PNG
                  </button>
                </div>
                
                <div className="mb-6 p-6 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl">
                  <h3 className="text-xl font-bold mb-2">Costo Total Anual de Nómina</h3>
                  <p className="text-5xl font-bold">{formatearMoneda(calcularCostoTotal())}</p>
                  <p className="text-lg mt-2 opacity-90">
                    Costo mensual: {formatearMoneda(calcularCostoTotal() / 12)}
                  </p>
                </div>

                <div ref={organigramaRef} className="bg-white p-6 rounded-lg border-2 border-green-200">
                  {dependencias.filter(d => d.parentId === null).map(dep => renderizarOrganigramaVisual(dep))}
                </div>
              </div>
            )}

            {vistaActual === 'presupuestoNivel' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-green-800">Presupuesto Detallado por Nivel</h2>
                  <button
                    onClick={exportarPresupuestoNivel}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
                  >
                    <Download size={20} /> Exportar Excel
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full bg-white rounded-lg overflow-hidden shadow-lg">
                    <thead className="bg-green-700 text-white">
                      <tr>
                        <th className="px-6 py-4 text-left">Nivel</th>
                        <th className="px-6 py-4 text-center">Número de Personas</th>
                        <th className="px-6 py-4 text-right">Salario Mensual</th>
                        <th className="px-6 py-4 text-right">Costo Mensual Total</th>
                        <th className="px-6 py-4 text-right">Costo Anual Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {niveles.map((nivel, index) => {
                        const totalPersonas = dependencias.reduce((sum, dep) => sum + (dep.personal[nivel.id] || 0), 0);
                        const costoMensual = totalPersonas * nivel.salario;
                        const costoAnual = costoMensual * 12;
                        
                        return (
                          <tr key={nivel.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="px-6 py-4 font-semibold text-green-800">
                              <div className="flex items-center gap-2">
                                <Users size={20} className="text-green-600" />
                                {nivel.nombre}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center font-medium">{totalPersonas}</td>
                            <td className="px-6 py-4 text-right">{formatearMoneda(nivel.salario)}</td>
                            <td className="px-6 py-4 text-right font-medium">{formatearMoneda(costoMensual)}</td>
                            <td className="px-6 py-4 text-right font-bold text-green-700">{formatearMoneda(costoAnual)}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-green-100 border-t-2 border-green-700">
                        <td className="px-6 py-4 font-bold text-green-900" colSpan="3">TOTAL GENERAL</td>
                        <td className="px-6 py-4 text-right font-bold text-green-900">
                          {formatearMoneda(calcularCostoTotal() / 12)}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-green-900 text-lg">
                          {formatearMoneda(calcularCostoTotal())}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {vistaActual === 'presupuestoDep' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-green-800">Presupuesto por Dependencia</h2>
                  <button
                    onClick={exportarPresupuestoDependencia}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
                  >
                    <Download size={20} /> Exportar Excel
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full bg-white rounded-lg overflow-hidden shadow-lg">
                    <thead className="bg-green-700 text-white">
                      <tr>
                        <th className="px-6 py-4 text-left">Dependencia</th>
                        <th className="px-6 py-4 text-center">Nivel Jerárquico</th>
                        <th className="px-6 py-4 text-center">Total Personal</th>
                        <th className="px-6 py-4 text-right">Costo Mensual</th>
                        <th className="px-6 py-4 text-right">Costo Anual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dependencias.map((dep, index) => {
                        const totalPersonal = Object.values(dep.personal).reduce((a, b) => a + b, 0);
                        const costoAnual = calcularCostoDependencia(dep.id);
                        const costoMensual = costoAnual / 12;
                        
                        return (
                          <tr key={dep.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="px-6 py-4 font-semibold text-green-800">{dep.nombre}</td>
                            <td className="px-6 py-4 text-center">
                              {dep.nivel === 1 ? 'Principal' : `Nivel ${dep.nivel}`}
                            </td>
                            <td className="px-6 py-4 text-center font-medium">{totalPersonal}</td>
                            <td className="px-6 py-4 text-right font-medium">{formatearMoneda(costoMensual)}</td>
                            <td className="px-6 py-4 text-right font-bold text-green-700">{formatearMoneda(costoAnual)}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-green-100 border-t-2 border-green-700">
                        <td className="px-6 py-4 font-bold text-green-900" colSpan="3">TOTAL GENERAL</td>
                        <td className="px-6 py-4 text-right font-bold text-green-900">
                          {formatearMoneda(calcularCostoTotal() / 12)}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-green-900 text-lg">
                          {formatearMoneda(calcularCostoTotal())}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <footer className="bg-green-800 text-white py-6 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-lg font-semibold">Desarrollado por Jasblleidy Pirazán</p>
          <p className="text-green-200 mt-1">Gestión y Administración Pública</p>
          <p className="text-green-200">Facultad de Ciencias Humanas y Económicas. Universidad Nacional de Colombia sede Medellín</p>
        </div>
      </footer>
    </div>
  );
}
